/**
 * Dateianhaenge in der Sicherung.
 *
 * Die Sicherung ist JSON, rohe Bytes passen dort nicht hinein. Anhaenge werden
 * deshalb beim Export nach Base64 gewandelt und beim Import zurueck.
 *
 * Sie WEGZULASSEN waere die einfachere Loesung und die falsche: eine
 * Datenkategorie, die das Backup stillschweigend auslaesst, bricht das
 * Kernversprechen der App - eine Datei, die alles enthaelt. Der Preis ist ein
 * Drittel mehr Groesse, begrenzt durch `domain/resources/attachments.ts`.
 */

/**
 * `String.fromCharCode(...bytes)` sprengt bei grossen Dateien den Aufrufstapel.
 * 32 kB je Durchgang ist sicher und schnell genug.
 */
const CHUNK = 0x8000;

/**
 * Bytes aus IndexedDB robust einsammeln.
 *
 * `instanceof Uint8Array` ist hier NICHT verlaesslich: was aus der Datenbank
 * kommt, wurde per `structuredClone` erzeugt und stammt je nach Umgebung aus
 * einem anderen Realm - der Prototyp stimmt dann nicht, die Daten schon.
 * Deshalb wird aus allem Array-Aehnlichen neu aufgebaut.
 */
export function toBytes(value: unknown): Uint8Array<ArrayBuffer> {
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (typeof value === 'object' && value !== null && 'length' in value) {
    return Uint8Array.from(value as ArrayLike<number>);
  }
  return new Uint8Array();
}

export function bytesToBase64(value: unknown): string {
  const bytes = toBytes(value);
  let binaer = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binaer += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binaer);
}

export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binaer = atob(base64);
  const bytes = new Uint8Array(binaer.length);
  for (let i = 0; i < binaer.length; i += 1) bytes[i] = binaer.charCodeAt(i);
  return bytes;
}

/** Fuer Vorschau und Download - erst hier entsteht ueberhaupt ein Blob. */
export function bytesToBlob(value: unknown, mimeType: string): Blob {
  return new Blob([toBytes(value)], { type: mimeType });
}

/** Anhang-Zeilen fuer die Sicherung: `bytes` wird zu einem Base64-String. */
export function encodeAttachmentRows(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return rows.map((row) => ({ ...row, bytes: bytesToBase64(row.bytes) }));
}

/** Zurueck aus der Sicherung: Base64-String wird wieder ein `Uint8Array`. */
export function decodeAttachmentRows(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return rows.map((row) => ({
    ...row,
    bytes: base64ToBytes(typeof row.bytes === 'string' ? row.bytes : ''),
  }));
}

/**
 * Sieht die Zeile nach einem brauchbaren Anhang aus?
 *
 * Wird beim Pruefen der Datei aufgerufen, BEVOR etwas geschrieben wird - ein
 * abgeschnittenes oder von Hand bearbeitetes JSON soll auffallen, solange der
 * bestehende Stand noch unberuehrt ist.
 */
export function isDecodableAttachment(row: Record<string, unknown>): boolean {
  if (typeof row.bytes !== 'string') return false;
  try {
    atob(row.bytes);
    return true;
  } catch {
    return false;
  }
}
